import styled from 'styled-components';

/* Visual tokens mirror the command center (AccountManagerDashboard): text
 * #0f172a, secondary #64748b, accent #2563eb, border #e2e8f0. */

export const Wrapper = styled.div`
  width: 100%;
  margin: 32px 0 64px;
  color: #0f172a;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
  flex-wrap: wrap;
`;

export const PageTitle = styled.h1`
  font-size: 20px;
  margin: 0;
  font-weight: 650;
  letter-spacing: -0.01em;
`;

export const SubTitle = styled.div`
  color: #64748b;
  font-size: 13px;
  margin-bottom: 16px;
`;

export const HeaderActions = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const HeaderButton = styled.button`
  border: 1px solid #2563eb;
  background: #fff;
  color: #2563eb;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #eff6ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const GhostButton = styled(HeaderButton)`
  border-color: #e2e8f0;
  color: #475569;

  &:hover {
    background: #f8fafc;
  }
`;

/* --- Three-pane layout --- */

export const Layout = styled.div`
  display: grid;
  grid-template-columns: 280px 300px minmax(0, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const Pane = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const PaneHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
`;

export const PaneSearch = styled.input`
  box-sizing: border-box;
  margin: 10px 12px 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 10px;
  font: inherit;
  font-size: 13px;
  color: #0f172a;
`;

export const PaneList = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: calc(100vh - 320px);
  min-height: 120px;
  padding: 6px 0;
`;

export const MainPane = styled.div`
  min-width: 0;
`;

/* --- List rows (groups / students) --- */

export const Row = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  background: ${({ active }) => (active ? '#eff6ff' : 'transparent')};
  box-shadow: ${({ active }) => (active ? 'inset 2px 0 0 #2563eb' : 'none')};
  padding: 8px 12px;
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ active }) => (active ? '#eff6ff' : '#f8fafc')};
  }
`;

export const RowMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  b {
    font-weight: 600;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    font-size: 11px;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const PinnedRow = styled(Row)`
  border-bottom: 1px solid #e2e8f0;
  padding-top: 10px;
  padding-bottom: 10px;

  b {
    font-weight: 650;
  }
`;

export const UnreadBubble = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 8px;
  white-space: nowrap;
  color: #fff;
  background: #2563eb;
`;

export const WarnBubble = styled(UnreadBubble)`
  background: #d97706;
`;

export const DangerBubble = styled(UnreadBubble)`
  background: #dc2626;
`;

/** Amber "waiting for a reply" marker on a row. */
export const UnansweredDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d97706;
`;

export const Muted = styled.div`
  font-size: 13px;
  color: #64748b;
  padding: 16px 12px;
`;

/** Centered placeholder for the main pane before a context is picked. */
export const EmptyState = styled.div`
  border: 1px dashed #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #64748b;
  font-size: 13px;
  text-align: center;
  padding: 64px 24px;
`;

/* --- Group mail list (whole-group Почта tab) --- */

export const MailRow = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: 0;
  background: transparent;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 4px;
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: 0;
  }
`;

export const Card = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  padding: 12px 16px;
`;

export const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin: 20px 0 4px;
`;

export const MailTime = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: #64748b;
  font-variant-numeric: tabular-nums;
`;
